import { v4 as uuidv4 } from 'uuid';
import CryptoJS from 'crypto-js';

const STORAGE_KEY = 'appointment_app_auth';
const USERS_KEY = 'appointment_app_users';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.initializeStorage();
  }

  initializeStorage() {
    if (!localStorage.getItem(USERS_KEY)) {
      // Create test account and empty users array
      const testUser = {
        id: uuidv4(),
        name: 'Test User',
        email: 'test@test.at',
        password: this.hashPassword('123'),
        createdAt: new Date().toISOString(),
        calendars: [],
        polls: []
      };
      localStorage.setItem(USERS_KEY, JSON.stringify([testUser]));
    }
  }

  hashPassword(password) {
    return CryptoJS.SHA256(password).toString();
  }

  generateToken(user) {
    const payload = {
      id: user.id,
      email: user.email,
      exp: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
    };
    return btoa(JSON.stringify(payload));
  }

  validateToken(token) {
    try {
      const payload = JSON.parse(atob(token));
      return payload.exp > Date.now() ? payload : null;
    } catch {
      return null;
    }
  }

  async register(name, email, password) {
    const users = JSON.parse(localStorage.getItem(USERS_KEY));
    
    if (users.find(u => u.email === email)) {
      throw new Error('E-Mail bereits registriert');
    }

    const user = {
      id: uuidv4(),
      name,
      email,
      password: this.hashPassword(password),
      createdAt: new Date().toISOString(),
      calendars: [],
      polls: []
    };

    users.push(user);
    localStorage.setItem(USERS_KEY, JSON.stringify(users));

    const token = this.generateToken(user);
    localStorage.setItem(STORAGE_KEY, token);

    const { password: _, ...userWithoutPassword } = user;
    this.currentUser = userWithoutPassword;
    return userWithoutPassword;
  }

  async login(email, password) {
    const users = JSON.parse(localStorage.getItem(USERS_KEY));
    const user = users.find(u => u.email === email);
    
    if (!user || user.password !== this.hashPassword(password)) {
      throw new Error('Ungültige Anmeldedaten');
    }

    const token = this.generateToken(user);
    localStorage.setItem(STORAGE_KEY, token);

    const { password: _, ...userWithoutPassword } = user;
    this.currentUser = userWithoutPassword;
    return userWithoutPassword;
  }

  async logout() {
    localStorage.removeItem(STORAGE_KEY);
    this.currentUser = null;
  }

  async getCurrentUser() {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) return null;

    const payload = this.validateToken(token);
    if (!payload) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const users = JSON.parse(localStorage.getItem(USERS_KEY));
    const user = users.find(u => u.id === payload.id);
    
    if (!user) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const { password: _, ...userWithoutPassword } = user;
    this.currentUser = userWithoutPassword;
    return userWithoutPassword;
  }

  isAuthenticated() {
    const token = localStorage.getItem(STORAGE_KEY);
    return token && this.validateToken(token) !== null;
  }

  // Clean up expired data (3 years after expiry)
  async cleanupExpiredData() {
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

    // Clean calendars
    const calendars = JSON.parse(localStorage.getItem('appointment_app_calendars') || '[]');
    const activeCalendars = calendars.filter(cal => {
      const createdDate = new Date(cal.createdAt);
      return createdDate > threeYearsAgo;
    });
    localStorage.setItem('appointment_app_calendars', JSON.stringify(activeCalendars));

    // Clean polls
    const polls = JSON.parse(localStorage.getItem('appointment_app_polls') || '[]');
    const activePolls = polls.filter(poll => {
      const createdDate = new Date(poll.createdAt);
      const deadlineDate = poll.settings.deadline ? new Date(poll.settings.deadline) : createdDate;
      deadlineDate.setFullYear(deadlineDate.getFullYear() + 3);
      return new Date() < deadlineDate;
    });
    localStorage.setItem('appointment_app_polls', JSON.stringify(activePolls));

    // Clean bookings
    const bookings = JSON.parse(localStorage.getItem('appointment_app_bookings') || '[]');
    const activeBookings = bookings.filter(booking => {
      const createdDate = new Date(booking.createdAt);
      return createdDate > threeYearsAgo;
    });
    localStorage.setItem('appointment_app_bookings', JSON.stringify(activeBookings));
  }
}

export const authService = new AuthService();